/* eslint-disable @typescript-eslint/no-explicit-any */
'use client'

import { useEffect } from 'react'
import Image from 'next/image'
import type { PageTheme, ThankYouPageConfig } from '@/../types/sale-page'
import { trackPurchase } from './TrackingScripts'

const CARRIER_NAME: Record<string, string> = {
  thailand_post: 'ไปรษณีย์ไทย', kerry: 'Kerry Express', flash: 'Flash Express',
  jnt: 'J&T Express', shopee_xpress: 'Shopee Xpress', lazada_express: 'Lazada Logistics',
  best: 'BEST Express', nim: 'NIM Express', alpha: 'Alpha Fast', inter: 'Inter Express',
  dhl: 'DHL', fedex: 'FedEx', ninja_van: 'Ninja Van', tp_logistics: 'TP Logistics', other: 'อื่นๆ',
}

interface Props {
  order: any
  bankInfo: any
  config: ThankYouPageConfig
  theme: PageTheme
  getImageUrl: (url: string | undefined) => string
  onBack: () => void
}

export default function InlineThankYou({ order, config, theme, getImageUrl, onBack }: Props) {
  // Fire purchase tracking event once on mount
  useEffect(() => {
    if (!order) return
    trackPurchase({
      id: order.id,
      orderNumber: order.orderNumber,
      total: Number(order.finalTotal ?? order.total),
      items: (order.items || []).map((i: any) => ({
        name: i.name,
        price: Number(i.price),
        quantity: i.quantity,
      })),
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="max-w-md mx-auto px-4 py-8 text-center">
      {/* Shop-configured image/GIF — same as the standalone thank-you page.
          When set it replaces the default checkmark (was never rendered
          here before, so a configured image silently didn't show). */}
      {config.image ? (
        <div className="relative w-full h-48 rounded-xl overflow-hidden mb-4">
          <Image src={getImageUrl(config.image)} alt="" fill className="object-cover" sizes="500px" />
        </div>
      ) : (
        <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${theme.primaryColor}15` }}>
          <svg className="w-8 h-8" style={{ color: theme.primaryColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}

      <h2 className="text-xl font-bold text-gray-900 mb-1">{config.title || 'สั่งซื้อสำเร็จ!'}</h2>
      {config.message && <p className="text-sm text-gray-500 mb-4" dangerouslySetInnerHTML={{ __html: config.message }} />}

      <div className="bg-gray-50 rounded-xl px-4 py-2 inline-block mb-4">
        <span className="text-xs text-gray-400">หมายเลข</span>
        <p className="text-base font-mono font-bold">{order.orderNumber}</p>
      </div>

      {/* Order summary */}
      {config.showOrderSummary && order.items && (
        <div className="bg-gray-50 rounded-xl p-4 text-left mb-4">
          {order.items.map((item: any, i: number) => (
            <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-100 last:border-0">
              <span>{item.name} x{item.quantity}</span>
              <span className="font-medium">{item.price.toLocaleString()}฿</span>
            </div>
          ))}
          <div className="border-t border-gray-200 mt-2 pt-2 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-400">ค่าสินค้า</span><span>{Number(order.total).toLocaleString()}฿</span></div>
            {Number(order.shippingFee ?? 0) > 0
              ? <div className="flex justify-between"><span className="text-gray-400">ค่าจัดส่ง</span><span>{Number(order.shippingFee).toLocaleString()}฿</span></div>
              : <div className="flex justify-between"><span className="text-gray-400">ค่าจัดส่ง</span><span className="text-green-600">ฟรี</span></div>}
            {Number(order.discountAmount ?? 0) > 0 && <div className="flex justify-between"><span className="text-gray-400">ส่วนลด</span><span className="text-red-500">-{Number(order.discountAmount).toLocaleString()}฿</span></div>}
            <div className="flex justify-between font-bold text-base pt-1 border-t border-gray-200">
              <span>รวม</span>
              <span style={{ color: theme.primaryColor }}>{Number(order.finalTotal ?? order.total).toLocaleString()}฿</span>
            </div>
          </div>
        </div>
      )}

      {order.paymentMethod === 'COD' && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-700 mb-4">
          ชำระเงินปลายทาง — จัดส่งภายใน 3-5 วัน
        </div>
      )}

      {/* Tracking number (shown after shop updates the order) */}
      {(order.trackingNumber || order.shippingCarrier) && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm mb-4 text-left">
          <p className="text-xs font-semibold text-slate-500 mb-1.5">ข้อมูลการจัดส่ง</p>
          {order.shippingCarrier && (
            <p className="text-slate-700">ขนส่ง: <strong>{CARRIER_NAME[order.shippingCarrier] || order.shippingCarrier}</strong></p>
          )}
          {order.trackingNumber && (
            <p className="text-slate-700">เลขพัสดุ: <strong className="font-mono">{order.trackingNumber}</strong></p>
          )}
        </div>
      )}

      {config.ctaLink && config.ctaLink.trim() ? (
        // When the shop set a CTA link, the button must OPEN IT (previously it
        // always called onBack, so a configured link did nothing). Normalise a
        // bare domain to https:// so it isn't treated as a relative path.
        <a
          href={normalizeCtaHref(config.ctaLink.trim())}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full py-3 text-center text-white font-medium rounded-xl transition hover:opacity-90"
          style={{ backgroundColor: theme.primaryColor }}
        >
          {config.ctaText || 'ดูเพิ่มเติม'}
        </a>
      ) : (
        <button onClick={onBack} className="w-full py-3 text-white font-medium rounded-xl transition hover:opacity-90" style={{ backgroundColor: theme.primaryColor }}>
          {config.ctaText || 'กลับหน้าร้าน'}
        </button>
      )}
    </div>
  )
}

// Accept bare domains ("lin.ee/xxx") as well as full URLs / tel: / mailto:.
function normalizeCtaHref(link: string): string {
  if (/^(https?:|tel:|mailto:)/i.test(link)) return link
  return `https://${link.replace(/^\/+/, '')}`
}
